import {
 createContext,
 useContext,
 useState,
 useEffect,
 useCallback,
 useMemo,
} from "react";
import { authenticationService } from "services";

const AuthenticationContext = createContext();

export function AuthenticationProvider({ children }) {
 const [user, setUser] = useState(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);

 const isAuthenticated = !!user;

 // 1. Memoize the status check function
 const checkAuthenticationStatus = useCallback(async () => {
  setLoading(true);
  setError(null);
  const result = await authenticationService.checkAuthenticationStatus();

  if (result.success && result.data.authenticated) {
   setUser({
    uid: result.data.uid,
    email: result.data.email,
    firstName: result.data.firstName,
    lastName: result.data.lastName,
    birthday: result.data.birthday || null,
    gender: result.data.gender || null,
    photoUrl: result.data.photoUrl || null,
    emailVerified: result.data.emailVerified || false,
    hasVendorAccess: result.data.hasVendorAccess || false,
    settings: result.data.settings || { theme: "light" },
    createdAt: result.data.createdAt || null,
    updatedAt: result.data.updatedAt || null,
   });
  } else {
   setUser(null);
   if (!result.success) setError(result.error);
  }
  setLoading(false);
 }, []); // No deps, stable service call

 // 2. Memoize SignIn/SignOut
 const signIn = useCallback((returnUrl = null) => {
  const url = returnUrl || window.location.href;
  authenticationService.redirectToSignIn(url);
 }, []);

 const signOut = useCallback(async () => {
  setLoading(true);
  setError(null);
  const result = await authenticationService.signOut();
  if (result.success) setUser(null);
  else setError(result.error);
  setLoading(false);
 }, []);

 const clearError = useCallback(() => {
  setError(null);
 }, []);

 useEffect(() => {
  checkAuthenticationStatus();
 }, [checkAuthenticationStatus]);

 // Guard against Safari ITP silently expiring the Firebase session
 // while React state still shows the user as logged in.
 // onAuthStateChanged fires when Firebase loses the token — we re-check
 // the server session to attempt a refresh, or clear the user if it truly expired.
 useEffect(() => {
  let unsubscribe = () => {};

  authenticationService.subscribeToFirebaseAuthState((firebaseUser) => {
   if (!firebaseUser) {
    // Firebase lost its session — re-validate against the server
    checkAuthenticationStatus();
   }
  }).then((unsub) => {
   unsubscribe = unsub;
  });

  return () => unsubscribe();
 }, []); // eslint-disable-line react-hooks/exhaustive-deps

 // 3. THE MAGIC: Memoize the Context Value
 const value = useMemo(
  () => ({
   user,
   isAuthenticated,
   loading,
   error,
   checkAuthenticationStatus,
   signIn,
   signOut,
   clearError,
  }),
  [
   user,
   isAuthenticated,
   loading,
   error,
   checkAuthenticationStatus,
   signIn,
   signOut,
   clearError,
  ],
 );

 return (
  <AuthenticationContext.Provider value={value}>
   {children}
  </AuthenticationContext.Provider>
 );
}

export function useAuthenticationContext() {
 const context = useContext(AuthenticationContext);
 if (!context) {
  throw new Error(
   "useAuthenticationContext must be used within an AuthenticationProvider",
  );
 }
 return context;
}
